import { sortByAisle } from './aisles.js';
import { roundToPacks } from './packs.js';
import { applyPantry } from './pantry.js';
import { householdPortions } from './portions.js';
import {
  aggregateRequirements, dinnerRequirements, lunchRequirements, requireIngredient,
  stapleRequirements, type Lookups,
} from './requirements.js';
import type { PackSize, Person, ShoppingLine, WeekPlan } from './types.js';

export interface BuildInput {
  plan: WeekPlan;
  people: Person[];
  lookups: Lookups;
  packSizes: PackSize[];
}

/** Key a pack list by store and ingredient so lookup is O(1) per line. */
export function indexPackSizes(packSizes: PackSize[]): Map<string, PackSize[]> {
  const index = new Map<string, PackSize[]>();
  for (const pack of packSizes) {
    const key = `${pack.store}:${pack.ingredientId}`;
    const existing = index.get(key);
    if (existing) existing.push(pack);
    else index.set(key, [pack]);
  }
  return index;
}

/**
 * Turn a week's plan into the shopping list.
 *
 * plan -> requirements -> aggregate -> cupboard -> packs -> aisles.
 * Pure: no clock, no I/O. Manual extras are a Phase 2 concern appended to the
 * persisted list, so this stays a faithful projection of the plan.
 */
export function buildShoppingList({ plan, people, lookups, packSizes }: BuildInput): ShoppingLine[] {
  const household = householdPortions(people);

  const aggregated = aggregateRequirements([
    ...dinnerRequirements(plan.dinners, household, lookups),
    ...lunchRequirements(plan.lunches, lookups, people),
    ...stapleRequirements(plan.staples, lookups),
  ]);

  // Cupboard check runs AFTER aggregation. Today applyPantry is a whole-line
  // filter, so the order is not observable — but the moment it ever deducts a
  // partial amount, running it first would drop a staple that two meals share.
  const needed = applyPantry(aggregated, lookups.ingredients, plan.pantry);
  const packIndex = indexPackSizes(packSizes);

  const lines: ShoppingLine[] = [];
  for (const req of needed) {
    // Zero is legitimate and quiet: a spice at 0.001g per portion rounds to 0.
    if (req.qty === 0) continue;
    if (!Number.isFinite(req.qty) || req.qty < 0) {
      throw new Error(
        `Ingredient "${req.ingredientId}" has an impossible quantity (${req.qty}), ` +
        `contributed by ${req.sources.join(', ')}. Every sibling module throws on a bad ` +
        `number rather than dropping a line — a line that silently vanishes reaches the ` +
        `checkout unnoticed, which is the worst failure this app has.`,
      );
    }
    const ingredient = requireIngredient(lookups, req.ingredientId, 'the shopping list');
    const packs = packIndex.get(`${plan.store}:${ingredient.id}`) ?? [];
    const choice = roundToPacks(req.qty, packs, ingredient.baseUnit);

    lines.push({
      ingredientId: ingredient.id,
      label: ingredient.name,
      requiredQty: req.qty,
      unit: ingredient.baseUnit,
      packLabel: choice?.label ?? null,
      isUnpacked: choice === null,
      aisle: ingredient.aisle,
      sources: req.sources,
    });
  }

  return sortByAisle(lines, plan.store);
}
